import {
  ApolloQueryResult,
  FetchPolicy,
  FetchResult,
  MutationOptions,
  ObservableQuery,
  OperationVariables,
  QueryOptions,
  SubscriptionOptions,
  WatchQueryOptions,
} from '@apollo/client/core';
import { Observable } from '@apollo/client/utilities';
import _cache from '@/config/chatCache';
import { WsClient } from '@/utils/wsClient';
import { AccountApi } from './account.api';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}
export interface ChatApiOptions {
  accountApi: AccountApi;
  onRefreshTokenError?: () => Promise<void>;
  refreshTokens?: () => Promise<AuthTokens>;
  requestPolicy?: FetchPolicy;
  subscriptionListener?: () => void;
}

export class ChatApi {
  private readonly accountApi: AccountApi;
  private client: WsClient;
  private onRefreshTokenError?: () => Promise<void>;

  constructor(opts: ChatApiOptions) {
    Object.assign(this, opts);
    this.client = new WsClient(this.accountApi, this.onRefreshTokenError);
  }

  public async query<
    TData = any,
    TVariables extends OperationVariables = Record<string, any>
  >(options: QueryOptions<TVariables>): Promise<ApolloQueryResult<TData>> {
    const { data, loading, error, networkStatus } = await this.client
      .instance()
      .query<TData, TVariables>(options);
    return { data, loading, error, networkStatus };
  }

  public async mutate<
    TData = any,
    TVariables extends OperationVariables = Record<string, any>
  >(options: MutationOptions<TData, TVariables>): Promise<FetchResult<TData>> {
    return await this.client.instance().mutate<TData, TVariables>(options);
  }

  public subscribe<
    TData = any,
    TVariables extends OperationVariables = Record<string, any>
  >(options: SubscriptionOptions<TVariables>): Observable<FetchResult<TData>> {
    options.fetchPolicy = 'cache-first';

    return new Observable((observer) =>
      this.client
        .instance()
        .subscribe(options)
        .subscribe({
          next: (data) => observer.next(data),
          error: (error) => observer.error(error),
          complete: () => observer.complete(),
        })
    );
  }

  public refetch<
    TData = any,
    TVariables extends OperationVariables = Record<string, any>
  >(
    options: WatchQueryOptions<TVariables>
  ): ObservableQuery<TData, TVariables> {
    options.fetchPolicy = 'cache-and-network';
    return this.client.instance().watchQuery<TData, TVariables>(options);
  }

  public unSubscribeAll(): void {
    this.client.instance().onResetStore(async () => console.log('done'));
  }
}

export const chatCache = _cache;
